import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Case } from '../domain/initial-cases.js';
import { listCasesPage, type CaseListParams } from '../api/cases.js';
import { useAppData } from './contexts/AppDataContext.js';
import { usePollTick } from './usePollTick.js';
import { usePrefs } from '../lib/prefs.js';

// H-2: each work-queue page drives its own scoped, paged server query
// instead of filtering the shared most-recent-50 window. Fetched rows are
// merged into the AppDataContext store (mergeCases) so the rest of the app —
// updateCase's If-Match chain, the selected-case detail view, lifecycle
// actions — keeps operating on one source of truth; this hook just tracks
// WHICH rows form the current page (ids) and the population size (total).
//
// `cases` is resolved against the live store on every render, so a PATCH
// that lands while the page is open re-renders the row without a refetch.

export interface CaseQueryResult {
  /** Page rows in server order, resolved against the live store. */
  cases: Case[];
  /** Full filtered population count (not the page size). */
  total: number;
  loading: boolean;
  error: string | null;
  offset: number;
  limit: number;
  setOffset: (offset: number) => void;
  refetch: () => void;
}

export function useCaseQuery(
  params: CaseListParams,
  opts: { limit?: number; pausePolling?: boolean } = {},
): CaseQueryResult {
  const { cases: storeCases, mergeCases } = useAppData();
  // #399 — the rows-per-page preference is the default page size for every
  // queue list. An explicit `opts.limit` still wins: a caller that names a
  // window is stating a requirement (a fixed-height panel, a "top N" widget),
  // not a taste. Subscribed rather than read once, so changing the setting
  // re-queries the list that is open instead of waiting for a navigation.
  //
  // usePrefs() is called UNCONDITIONALLY, and the choice made after it. The
  // shorter `opts.limit ?? usePrefs().rowsPerPage` skips the hook whenever a
  // caller passes a limit, which changes hook order between renders and
  // crashes React — and nothing here would catch it: no call site passes
  // `limit` today, and eslint-plugin-react-hooks is loaded in this package
  // with none of its rules enabled. The comment above actively invites such a
  // caller, so the hazard has to be closed rather than noted.
  const prefs = usePrefs();
  const limit = opts.limit ?? prefs.rowsPerPage;
  const [offset, setOffset] = useState(0);
  const [ids, setIds] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchNonce, setFetchNonce] = useState(0);
  // #474 — this list refetches on the #148 heartbeat, so rows created out of
  // band (another analyst's decision, a customer-API write) stop being
  // invisible until a reload. `pausePolling` lets a caller hold the tick off
  // while it has a mutation in flight.
  const pollTick = usePollTick(opts.pausePolling ?? false);
  // Guards against an in-flight background fetch resolving AFTER a newer
  // foreground one (a filter change, a page turn) and overwriting the fresher
  // page with staler rows.
  const seqRef = useRef(0);

  // Stable identity for the params object so callers can pass a fresh
  // literal each render without retriggering the fetch effect.
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    // New query → first page. (Also covers the initial mount, where it's a
    // no-op.)
    setOffset(0);
  }, [paramsKey]);

  // One fetch path, two callers. `background` is the poll: it must not show a
  // spinner, must not surface an error, and must not disturb what is on screen
  // if it fails — a populated table replaced by an error banner because one
  // 60-second refresh 503'd is worse than the staleness it was fixing.
  const runQuery = useCallback(async (background: boolean) => {
    const seq = ++seqRef.current;
    if (!background) setLoading(true);
    const parsed = JSON.parse(paramsKey) as CaseListParams;
    try {
      const { items, total: count } = await listCasesPage({ ...parsed, limit, offset });
      if (seq !== seqRef.current) return;
      mergeCases(items);
      setIds(items.map(c => c.id));
      setTotal(count);
      setError(null);
    } catch (err) {
      if (seq !== seqRef.current || background) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      // NOT `&& !background`. A foreground run that a background tick has
      // superseded bails on the seq check above and never clears `loading`, so
      // if the background run could not clear it either, the flag stuck at true
      // until the next foreground fetch. A background run never SETS loading
      // (see the guard at the top), so it can only ever clear it — and if it is
      // the current seq it has just delivered fresh rows, which is precisely
      // when loading should be false.
      if (seq === seqRef.current) setLoading(false);
    }
  }, [paramsKey, limit, offset, mergeCases]);

  useEffect(() => {
    void runQuery(false);
  }, [runQuery, fetchNonce]);

  // Deliberately keyed on pollTick ALONE. `runQuery`'s identity changes with
  // offset/params, and listing it as a dependency would fire a background
  // refetch on every page turn on top of the foreground one above. The effect
  // still closes over the CURRENT render's `runQuery`, so a refresh reloads
  // the page the operator is looking at and leaves the offset where they left
  // it.
  useEffect(() => {
    if (pollTick === 0) return;
    void runQuery(true);
  }, [pollTick]);

  const cases = useMemo(() => {
    const byId = new Map(storeCases.map(c => [c.id, c]));
    return ids.flatMap(id => {
      const c = byId.get(id);
      return c ? [c] : [];
    });
  }, [ids, storeCases]);

  const refetch = useCallback(() => setFetchNonce(n => n + 1), []);

  return { cases, total, loading, error, offset, limit, setOffset, refetch };
}

// Merges a query's page rows with store rows that match the same predicate,
// deduped by id, page order first. Sections whose membership changes through
// LOCAL actions (e.g. "In Progress" re-vets after initiateReVet) render the
// union so a just-transitioned case appears/disappears immediately instead
// of waiting for a refetch; the predicate also drops page rows that no
// longer match after a mutation. Store extras are only appended on the FIRST
// page (offset 0) — deeper pages stay pure server slices, otherwise a store
// row would repeat on every page.
export function unionMatching(
  pageRows: Case[],
  storeRows: Case[],
  predicate: (c: Case) => boolean,
  offset = 0,
): Case[] {
  const seen = new Set<string>();
  const out: Case[] = [];
  const candidates = offset === 0 ? [...pageRows, ...storeRows] : pageRows;
  for (const c of candidates) {
    if (seen.has(c.id) || !predicate(c)) continue;
    seen.add(c.id);
    out.push(c);
  }
  return out;
}

// Debounce for the server-driven search box: state updates keep the input
// responsive; the query only fires once typing pauses.
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
